/**
 * Notifications — Network > Notifications (org-scoped alert delivery).
 *
 * Where this tenant's health alerts are delivered: email, a generic webhook, or a
 * Slack/Mattermost incoming webhook. The health-monitor loop fires to every enabled channel
 * when an alert is raised or resolved. Each row shows delivery health (last ok / last error)
 * and a "Send test" that proves the channel before you rely on it.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useNotificationChannels,
  useCreateChannel,
  useUpdateChannel,
  useDeleteChannel,
  useTestChannel,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type { NotificationChannel, NotificationKind } from "@/types/domain";
import { relTime } from "@/lib/format";
import { channelFormError, channelUpdateBody } from "@/lib/channelTarget";

const KIND_LABEL: Record<NotificationKind, string> = {
  email: "Email",
  webhook: "Webhook",
  slack: "Slack / Mattermost",
};
const KIND_HINT: Record<NotificationKind, string> = {
  email: "email address (needs controller SMTP configured)",
  webhook: "https:// URL — receives the full alert JSON",
  slack: "Slack/Mattermost incoming-webhook URL",
};

export default function Notifications() {
  const { data, isLoading, error } = useNotificationChannels();
  const [editing, setEditing] = useState<NotificationChannel | null>(null);
  const [creating, setCreating] = useState(false);
  const channels = data?.items ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Notifications" subtitle="Deliver health alerts to email, webhook, or Slack" />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>Add channel</Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading channels…</div>}

      {data && channels.length === 0 && (
        <EmptyState
          title="No notification channels yet"
          hint="Add a channel and every raised/resolved alert for this customer is delivered there. Send a test to confirm it works before you rely on it."
        />
      )}

      {channels.length > 0 && (
        <ul className="flex flex-col gap-2">
          {channels.map((c) => (
            <ChannelRow key={c.id} channel={c} onEdit={() => setEditing(c)} />
          ))}
        </ul>
      )}

      {creating && <ChannelDialog onClose={() => setCreating(false)} />}
      {editing && <ChannelDialog channel={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ChannelRow({ channel, onEdit }: { channel: NotificationChannel; onEdit: () => void }) {
  const test = useTestChannel();
  const result = test.data;
  return (
    <li className={`md-card flex items-center gap-3 p-3 ${channel.enabled ? "" : "opacity-60"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{channel.name}</span>
          <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-ink-subtle">
            {KIND_LABEL[channel.kind]}
          </span>
          {!channel.enabled && (
            <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
              Disabled
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">
          {/* OP#1917: display-only mask; the server never returns the real target. */}
          <span className="truncate font-mono" data-testid="channel-target-masked">
            {channel.has_target ? channel.target_masked : "no target"}
          </span>
          {channel.last_error ? (
            <span className="text-critical">last error: {channel.last_error}</span>
          ) : channel.last_ok_at ? (
            <span className="text-healthy">last ok {relTime(channel.last_ok_at)}</span>
          ) : (
            <span className="text-ink-subtle">never sent</span>
          )}
          {result && (
            <span className={result.ok ? "text-healthy" : "text-critical"}>
              {result.ok ? "test sent ✓" : `test failed: ${result.error ?? "error"}`}
            </span>
          )}
        </div>
      </div>
      <RoleGate min="org_admin" mode="hide">
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" disabled={test.isPending} onClick={() => test.mutate(channel.id)}
            title="Send a synthetic test notification to this channel">
            {test.isPending ? "Sending…" : "Send test"}
          </Button>
          <Button variant="secondary" onClick={onEdit}>Edit</Button>
        </div>
      </RoleGate>
    </li>
  );
}

function ChannelDialog({ channel, onClose }: { channel?: NotificationChannel; onClose: () => void }) {
  const create = useCreateChannel();
  const update = useUpdateChannel();
  const del = useDeleteChannel();
  const editing = !!channel;

  const [kind, setKind] = useState<NotificationKind>(channel?.kind ?? "webhook");
  const [name, setName] = useState(channel?.name ?? "");
  // OP#1917: on edit this is the REPLACEMENT target and starts empty. The stored target is never
  // sent to the browser, and the mask must never be pre-filled here (saving it would overwrite
  // the real URL with "…"; the server 400s that too).
  const [target, setTarget] = useState("");
  const [enabled, setEnabled] = useState(channel?.enabled ?? true);
  const [touched, setTouched] = useState(false);

  const pending = create.isPending || update.isPending;
  const err = create.isError || update.isError;
  const formError = channelFormError({ editing, name, target });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (formError) return;
    const onDone = { onSuccess: onClose };
    if (editing) {
      // target only when a replacement was typed -> otherwise the server keeps the stored one.
      update.mutate({ id: channel!.id, ...channelUpdateBody({ name, replaceTarget: target, enabled }) }, onDone);
    } else {
      create.mutate({ kind, name: name.trim(), target: target.trim(), enabled }, onDone);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {editing ? `Edit ${channel!.name}` : "Add notification channel"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Alerts raised or resolved for this customer are delivered here.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Type</span>
              <select
                value={kind}
                disabled={editing}
                onChange={(e) => setKind(e.target.value as NotificationKind)}
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand read-only:opacity-70"
              >
                <option value="email">Email</option>
                <option value="webhook">Webhook</option>
                <option value="slack">Slack / Mattermost</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ops on-call"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
            </label>
            {editing && (
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Current target</span>
                <span data-testid="channel-current-target"
                  className="rounded border border-line bg-surface-2 px-3 py-2 font-mono text-ink-muted">
                  {channel!.has_target ? channel!.target_masked : "none"}
                </span>
              </div>
            )}
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">{editing ? "Replace target" : "Target"}</span>
              <input value={target} onChange={(e) => setTarget(e.target.value)}
                data-testid="channel-target-input"
                autoComplete="off"
                placeholder={editing ? "Leave empty to keep the current target"
                  : kind === "email" ? "oncall@example.com" : "https://…"}
                className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
              <span className="text-[0.6875rem] text-ink-subtle">
                {editing ? `Hidden for security. Paste a full new ${KIND_HINT[kind]} to replace it.` : KIND_HINT[kind]}
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Enabled</span>
            </label>

            {touched && formError && (
              <span className="text-xs text-critical" data-testid="channel-form-error">{formError}</span>
            )}
            {err && <span className="text-xs text-critical">Could not save the channel.</span>}
            <div className="md-dialog-footer flex items-center justify-between gap-2">
              {editing ? (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="danger" type="button" disabled={del.isPending}
                    onClick={() => del.mutate(channel!.id, { onSuccess: onClose })}>
                    {del.isPending ? "…" : "Delete"}
                  </Button>
                </RoleGate>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={pending}>
                  {pending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
